import React, { useState, useEffect, useMemo } from "react";
import { Modal, Button, Progress } from "antd";
import { Document, Page, pdfjs } from "react-pdf";

// PDF.js配置
pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const FilePreviewModal = (props) => {
  const { onClose, open, href } = props;
  const [isLoading, setLoading] = useState(false);
  const [numPages, setNumPages] = useState(null);
  const [pageWidth, setPageWidth] = useState(1000);

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width < 768) {
        setPageWidth(width - 40);
      } else if (width >= 768 && width < 1024) {
        setPageWidth(700);
      } else {
        setPageWidth(1000);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const getPageWidth = () => {
    const width = window.innerWidth;
    if (width < 768) {
      return width - 40;
    } else if (width >= 768 && width < 1024) {
      return 700;
    }
    return 1000;
  };

  const getFileType = () => {
    if (!href) return "";

    if (href.startsWith("data:image/")) {
      return "image";
    }

    try {
      const url = new URL(href);
      const pathname = url.pathname;
      const parts = pathname.split(".");
      if (parts.length > 1) {
        const ext = parts.pop().toLowerCase();
        return ext;
      }
    } catch (e) {
      const parts = href.split(".");
      if (parts.length > 1) {
        const ext = parts.pop().toLowerCase();
        return ext;
      }
    }

    return "";
  };

  const fileType = useMemo(() => getFileType(), [href]);

  const renderPreview = () => {
    const type = getFileType();
    const isMobile = window.innerWidth < 768;

    switch (type) {
      case "pdf":
        return (
          <Document
            file={href}
            onLoadSuccess={({ numPages }) => setNumPages(numPages)}
            renderMode="canvas"
            loading={
              <div
                style={{
                  height: "calc(100vh - 30px)",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Progress type="circle" percent={0} />
              </div>
            }
            error="Failed to load PDF"
            onLoad={() => setLoading(false)}
            onError={() => setLoading(false)}
          >
            {Array.from(new Array(numPages), (_, index) => (
              <Page
                width={getPageWidth()}
                key={`page_${index + 1}`}
                pageNumber={index + 1}
              />
            ))}
          </Document>
        );

      case "doc":
      case "docx":
      case "xls":
      case "xlsx":
      case "ppt":
      case "pptx":
        return (
          <iframe
            title="Office Preview"
            src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
              href
            )}`}
            width="100%"
            height={isMobile ? "400px" : "600px"}
            frameBorder="0"
            style={{ border: "none" }}
            onLoad={() => setLoading(false)}
            onError={() => setLoading(false)}
          ></iframe>
        );

      case "txt":
        return (
          <iframe
            title="Text Preview"
            src={href}
            width="100%"
            height={isMobile ? "400px" : "600px"}
            frameBorder="0"
            style={{ border: "none" }}
            onLoad={() => setLoading(false)}
            onError={() => setLoading(false)}
          ></iframe>
        );

      case "jpg":
      case "jpeg":
      case "png":
      case "gif":
      case "bmp":
      case "webp":
        return (
          <img
            src={href}
            alt="preview"
            style={{
              width: "100%",
              height: isMobile ? "auto" : "600px",
              objectFit: "contain",
            }}
            onLoad={() => setLoading(false)}
            onError={() => setLoading(false)}
          />
        );

      case "mp4":
        return (
          <video
            controls
            autoPlay
            width="100%"
            height={isMobile ? "400px" : "600px"}
            onLoad={() => setLoading(false)}
            onError={() => setLoading(false)}
          >
            <source src={href} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        );

      case "mp3":
        return (
          <audio
            controls
            autoPlay
            onLoad={() => setLoading(false)}
            onError={() => setLoading(false)}
          >
            <source src={href} type="audio/mp3" />
            Your browser does not support the audio tag.
          </audio>
        );

      default:
        const supportedMediaTypes = ["avi", "mov", "mkv", "flv", "wmv", "ogg"];
        if (supportedMediaTypes.includes(type || "")) {
          return (
            <iframe
              title="Media Preview"
              src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
                href
              )}`}
              width="100%"
              height={isMobile ? "80px" : "100px"}
              frameBorder="0"
              style={{ border: "none" }}
              onLoad={() => setLoading(false)}
              onError={() => setLoading(false)}
            ></iframe>
          );
        }
        return (
          <iframe
            title="Webpage Preview"
            src={href}
            width="100%"
            height={isMobile ? "400px" : "600px"}
            frameBorder="0"
            style={{ border: "none" }}
            onLoad={() => setLoading(false)}
            onError={() => setLoading(false)}
          ></iframe>
        );
    }
  };

  const getModalWidth = () => {
    if (
      ["avi", "mov", "mkv", "flv", "wmv", "mp3", "ogg"].includes(fileType || "")
    ) {
      return 460;
    }
    return 1080;
  };

  return (
    <Modal
      width={getModalWidth()}
      visible={open}
      onCancel={onClose}
      footer={null}
      closable={false}
      bodyStyle={{
        padding: "0",
        minHeight: "100px",
        textAlign: "center",
      }}
      style={{ maxWidth: "100%", borderRadius: "15px" }}
    >
      <div
        style={{
          padding:
            window.innerWidth < 768 ? "15px 15px 30px" : "24px 30px 40px",
          position: "relative",
          fontWeight: "700",
          fontSize: window.innerWidth < 768 ? "20px" : "24px",
          lineHeight: window.innerWidth < 768 ? "28px" : "34.75px",
        }}
      >
        <Button
          type="link"
          icon="close"
          onClick={onClose}
          style={{
            position: "absolute",
            right: 10,
            top: 10,
            fontSize: window.innerWidth < 768 ? "20px" : "26px",
            flexShrink: 0,
            color: "#000",
          }}
        />
      </div>
      <div style={{ padding: "30px 20px" }}>
        {isLoading && (
          <div
            style={{
              position: "relative",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "200px",
            }}
          >
            <Progress type="circle" percent={0} />
          </div>
        )}
        {!isLoading && href && renderPreview()}
      </div>
    </Modal>
  );
};

export default FilePreviewModal;
